<script setup>
import { ref, reactive, computed, onMounted, onActivated, watch } from 'vue'
import api from '@/services/api'
import PaginaAdministrar from '@/components/base/grid/PaginaAdministrar.vue'
import AdminTable from '@/components/base/grid/AdminTable.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { useAuthStore } from '@/stores/auth'
import { usePaginacao } from '@/composables/usePaginacao'
import { useUI } from '@/composables/useUI'

const emit = defineEmits(['adicionar-item', 'editar-item', 'show-notification'])

const authStore = useAuthStore()
const { obterClasseStatusGhost, obterCorDinamica } = useUI()
const { paginacao, injetarParametros, atualizarPaginacao } = usePaginacao()

const contas = ref([])
const loading = ref(false)
const erro = ref(null)
const filtrosAbertos = ref(false)

const sortField = ref('')
const sortOrder = ref('')

const listas = reactive({
  status: [],
  moedas: [],
  instituicoes: []
})

const filtros = reactive({
  busca: '',
  idInstituicaoFinanceira: '',
  lstStatus: ''
})

const mostrarAuditoria = ref(false)
const auditoriaSelecionada = reactive({ id: null, nome: '' })

const temPermissaoCompleta = computed(() => authStore.pode('CONTAS_BANCARIAS_COMPLETO'))
const temPermissaoVisualizacao = computed(() => authStore.pode('CONTAS_BANCARIAS_VISUALIZACAO'))
const podeVerAcoes = computed(() => temPermissaoCompleta.value || temPermissaoVisualizacao.value)

const colunas = computed(() => {
  const cols = [
    { field: 'idContaBancaria', label: 'ID', width: 80, sortable: true, slotName: 'id' },
    { field: 'descricao', label: 'Descrição', sortable: true, class: 'fw-medium' },
    { field: 'nomeInstituicaoFinanceira', label: 'Banco', sortable: true },
    { field: 'agencia', label: 'Agência / Conta', sortable: true, slotName: 'agenciaConta' },
    { field: 'lstMoeda', label: 'Moeda', align: 'center', sortable: true, slotName: 'moeda' },
    { field: 'lstStatus', label: 'Status', align: 'center', sortable: true, slotName: 'status' }
  ]
  if (podeVerAcoes.value) {
    cols.push({ field: 'acoes', label: 'Ações', width: 120, align: 'right', slotName: 'acoes', resizable: false, sortable: false })
  }
  return cols
})

const sortResolver = (field, order, data) => {
  if (!field.startsWith('lst')) return null

  const resolvers = {
    lstMoeda: (row) => getMoedaTexto(row.lstMoeda),
    lstStatus: (row) => getStatusTexto(row.lstStatus)
  }

  const resolver = resolvers[field]
  if (!resolver) return null

  return [...data].sort((a, b) => {
    const cmp = resolver(a).localeCompare(resolver(b), 'pt-BR')
    return order === 'asc' ? cmp : -cmp
  })
}

const handleSortChange = () => {
  buscarDados(1)
}

watch(() => authStore.empresaAtiva, (novaEmpresa) => {
  if (novaEmpresa) {
    buscarDados(1)
  } else {
    contas.value = []
    paginacao.value.TotalCount = 0
  }
})

const carregarListas = async () => {
  try {
    const [resStatus, resMoedas, resInst] = await Promise.all([
      api.get('/ListasSistema/lookup/ST'),
      api.get('/ListasSistema/lookup/MOE'),
      api.get('/Parceiros?lstTipoPessoa=PARCEIRO_TIPO5&PageSize=500')
    ])
    listas.status = resStatus.data
    listas.moedas = resMoedas.data
    listas.instituicoes = resInst.data
  } catch (e) {
    console.warn('Erro ao carregar listas auxiliares:', e)
  }
}

const getStatusTexto = (idStatus) => {
  if (!idStatus) return '-'
  const obj = listas.status.find(s => s.idListaSistemaItem === idStatus || s.idListaClienteItem === idStatus)
  return obj ? obj.textoLista : idStatus
}

const getMoedaTexto = (idMoeda) => {
  if (!idMoeda) return '-'
  const obj = listas.moedas.find(m => m.idListaSistemaItem === idMoeda || m.idListaClienteItem === idMoeda)
  return obj ? obj.textoLista : idMoeda
}

const buscarDados = async (pageNumber = 1) => {
  if (authStore.isAdminInterno && !authStore.empresaAtiva) {
    erro.value = 'Por favor, selecione uma Empresa no topo da página.'
    contas.value = []
    return
  }

  loading.value = true
  erro.value = null

  try {
    const params = injetarParametros(pageNumber)

    if (authStore.empresaAtiva) params.append('idEmpresa', authStore.empresaAtiva)
    if (filtros.busca) params.append('busca', filtros.busca)
    if (filtros.idInstituicaoFinanceira) params.append('idInstituicaoFinanceira', filtros.idInstituicaoFinanceira)
    if (filtros.lstStatus) params.append('lstStatus', filtros.lstStatus)
    if (sortField.value) params.append('sortField', sortField.value)
    if (sortOrder.value) params.append('sortOrder', sortOrder.value)

    const response = await api.get(`/ContasBancarias?${params.toString()}`)
    contas.value = response.data
    atualizarPaginacao(response.headers)
  } catch (error) {
    console.error('Erro ao buscar contas bancárias:', error)
    erro.value = 'Não foi possível carregar os dados das contas bancárias.'
    contas.value = []
    paginacao.value.TotalCount = 0
  } finally {
    loading.value = false
  }
}

const aplicarFiltros = () => { buscarDados(1) }
const limparFiltros = () => { filtros.busca = ''; filtros.idInstituicaoFinanceira = ''; filtros.lstStatus = ''; buscarDados(1) }

const mudarPagina = (page) => {
  if (page >= 1 && page <= (paginacao.value.TotalPages || 1)) buscarDados(page)
}

const mudarTamanhoPagina = (novoTamanho) => {
  paginacao.value.PageSize = novoTamanho
  buscarDados(1)
}

const editarConta = (conta) => { emit('editar-item', conta) }

const abrirAuditoria = (conta) => {
  auditoriaSelecionada.id = conta.idContaBancaria
  auditoriaSelecionada.nome = conta.descricao
  mostrarAuditoria.value = true
}

onMounted(async () => {
  await carregarListas()
  if (authStore.empresaAtiva || !authStore.isAdminInterno) buscarDados(1)
})

onActivated(() => { buscarDados(paginacao.value.CurrentPage || 1) })

defineExpose({ buscarDados })
</script>

<template>
  <PaginaAdministrar
    titulo="Contas Bancárias da Empresa"
    subtitulo="Gestão das contas para transações financeiras"
    v-model:filtros-abertos="filtrosAbertos"
    :total-itens="paginacao.TotalCount"
    :itens-por-pagina="paginacao.PageSize"
    :pagina-atual="paginacao.CurrentPage"
    @mudar-pagina="mudarPagina"
    @mudar-tamanho-pagina="mudarTamanhoPagina"
    @adicionar="emit('adicionar-item')"
    @atualizar="aplicarFiltros"
    :mostrar-botao-adicionar="temPermissaoCompleta"
  >
    <template #acoes="{ toggleFiltros }">
      <button class="btn btn-dark" @click="toggleFiltros">
        <i class="bi bi-filter-lg me-2"></i> Filtros
      </button>
    </template>

    <template #filtros>
      <div class="row g-3 align-items-end">
        <div class="col-md-4">
          <label class="form-label">Descrição ou Número</label>
          <input type="text" class="form-control" v-model="filtros.busca" placeholder="Buscar..." @keydown.enter.prevent="aplicarFiltros">
        </div>
        <div class="col-md-3">
          <label class="form-label">Banco</label>
          <select class="form-select" v-model="filtros.idInstituicaoFinanceira" @change="aplicarFiltros">
            <option value="">Todos os Bancos</option>
            <option v-for="b in listas.instituicoes" :key="b.idParceiro" :value="b.idParceiro">{{ b.nomeFantasia || b.razaoSocial }}</option>
          </select>
        </div>
        <div class="col-md-3">
          <label class="form-label">Status</label>
          <select class="form-select" v-model="filtros.lstStatus" @change="aplicarFiltros">
            <option value="">Todos</option>
            <option v-for="s in listas.status" :key="s.idListaSistemaItem" :value="s.idListaSistemaItem">{{ s.textoLista }}</option>
          </select>
        </div>
        <div class="col-auto">
          <button class="btn btn-secondary me-2" @click="limparFiltros">Limpar</button>
          <button class="btn btn-primary" @click="aplicarFiltros">Aplicar Filtros</button>
        </div>
      </div>
    </template>

    <div v-if="loading" class="text-center p-5"><div class="spinner-border text-primary"></div></div>
    <div v-else-if="erro" class="alert alert-danger m-3">{{ erro }}</div>

    <AdminTable
      v-else
      :columns="colunas"
      :data="contas"
      :loading="loading"
      empty-message="Nenhuma conta bancária encontrada."
      row-key="idContaBancaria"
      v-model:sort-field="sortField"
      v-model:sort-order="sortOrder"
      :sort-resolver="sortResolver"
      @sort-change="handleSortChange"
      @row-dblclick="editarConta($event.row)"
    >
      <template #id="{ row }">
        <span class="text-muted fw-medium">#{{ row.idContaBancaria }}</span>
      </template>
      <template #agenciaConta="{ row }">
        {{ row.agencia }} / {{ row.numeroConta }}
      </template>

      <template #moeda="{ row }">
        <span class="badge" :class="obterCorDinamica(row.lstMoeda, listas.moedas)">
          {{ getMoedaTexto(row.lstMoeda) }}
        </span>
      </template>

      <template #status="{ row }">
        <span :class="obterClasseStatusGhost(getStatusTexto(row.lstStatus))">
          {{ getStatusTexto(row.lstStatus) }}
        </span>
      </template>

      <template #acoes="{ row }">
        <div class="acoes-wrapper" v-if="podeVerAcoes">
          <button class="btn-icon" @click="abrirAuditoria(row)" title="Ver Histórico"><i class="bi bi-clock-history"></i></button>
          <button class="btn-icon text-primary" @click="editarConta(row)" title="Editar"><i class="bi bi-pencil-fill"></i></button>
        </div>
      </template>
    </AdminTable>
  </PaginaAdministrar>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="ContaBancaria"
    :chave-primaria="auditoriaSelecionada.id"
    :titulo-registro="auditoriaSelecionada.nome"
    :dicionarios="{ 'lstStatus': listas.status, 'lstMoeda': listas.moedas }"
    @fechar="mostrarAuditoria = false"
  />
</template>

<style scoped>
</style>
